// apps/dashboard/src/components/chrome/DashboardShell.tsx
import { useEffect, useState, type ReactNode } from "react";
import { Link, useRouterState } from "@tanstack/react-router";
import { Menu } from "lucide-react";
import { DashboardFooter } from "./DashboardFooter";
import { SidebarNav } from "./SidebarNav";
import { AsyncBannerStack } from "@/design-system/AsyncBannerStack";
import { LogoMark } from "@/design-system";
import { LiveScanStrip } from "@/components/targets/scans/LiveScanStrip";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "gated:sidebar-collapsed";

interface DashboardShellProps {
  children: ReactNode;
}

export function DashboardShell({ children }: DashboardShellProps) {
  // Desktop-only: the static sidebar's collapsed/expanded preference.
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  });
  // Below lg the sidebar is an off-canvas drawer toggled by the top bar.
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    window.localStorage.setItem(STORAGE_KEY, collapsed ? "1" : "0");
  }, [collapsed]);

  // Any navigation dismisses the mobile drawer so the destination shows.
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  useEffect(() => {
    setMobileOpen(false);
  }, [pathname]);

  // Escape closes the drawer while it's open.
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setMobileOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [mobileOpen]);

  return (
    <div className="h-screen bg-bg-2 flex flex-col">
      <MobileTopBar onOpenNav={() => setMobileOpen(true)} />
      <div className="flex flex-row flex-1 overflow-hidden">
        {/* Static sidebar — desktop only. */}
        <div
          className={cn(
            "hidden lg:block shrink-0",
            collapsed ? "lg:w-16" : "lg:w-64",
          )}
        >
          <SidebarNav
            collapsed={collapsed}
            onToggle={() => setCollapsed((c) => !c)}
          />
        </div>

        {/* Off-canvas drawer — hidden at lg and up. */}
        <MobileDrawer open={mobileOpen} onClose={() => setMobileOpen(false)} />

        <main className="flex-1 overflow-hidden bg-bg-2 flex flex-col">
          <LiveScanStrip />
          <AsyncBannerStack />
          {children}
        </main>
      </div>
      <DashboardFooter />
    </div>
  );
}

interface MobileTopBarProps {
  onOpenNav: () => void;
}

// Shown only below lg, where there's no room for a persistent sidebar.
function MobileTopBar({ onOpenNav }: MobileTopBarProps) {
  return (
    <div className="lg:hidden flex items-center gap-2 h-13 shrink-0 border-b border-border-1 bg-bg-1 px-3">
      <button
        type="button"
        onClick={onOpenNav}
        aria-label="Open navigation"
        className="size-8 flex items-center justify-center rounded-2 text-fg-2 hover:bg-bg-2 hover:text-fg-1 transition-colors"
      >
        <Menu size={18} strokeWidth={1.8} />
      </button>
      <Link to="/dashboard" className="flex items-center gap-2 text-fg-1">
        <LogoMark size={18} />
        <span className="text-body font-semibold tracking-[-0.01em]">Gated</span>
      </Link>
    </div>
  );
}

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Kept mounted so the slide and backdrop fade can animate both ways; the
// wrapper drops pointer events when closed so the off-screen panel is inert.
function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  return (
    <div
      className={cn("lg:hidden", !open && "pointer-events-none")}
      aria-hidden={!open}
    >
      <div
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-black/40 transition-opacity duration-200",
          open ? "opacity-100" : "opacity-0",
        )}
      />
      <div
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-64 transition-transform duration-200 ease-out",
          open ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <SidebarNav collapsed={false} />
      </div>
    </div>
  );
}
